/**
 * Shared default configuration for Recharts-based charts.
 *
 * Centralises animation, height, axis, tooltip, and CartesianGrid
 * settings so every chart renders consistently without repeating
 * the same props.
 */

// ---------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------

/** Disable animation by default — keeps charts snappy and avoids
 *  layout-shift on data updates. */
export const CHART_ANIMATION_ACTIVE = false;

// ---------------------------------------------------------------------------
// Heights
// ---------------------------------------------------------------------------

/** Standard chart heights (px) used across the dashboard. */
export const CHART_HEIGHTS = {
    /** Compact charts in cards / sidebars. */
    sm: 250,
    /** Default chart height. */
    md: 350,
    /** Full-width insight charts. */
    lg: 400,
} as const;

export type ChartHeightTier = keyof typeof CHART_HEIGHTS;

// ---------------------------------------------------------------------------
// CartesianGrid
// ---------------------------------------------------------------------------

/** Default props for `<CartesianGrid />`. Horizontal-only dashed grid lines
 *  with a subdued stroke so they don't compete with the data. */
export const CARTESIAN_GRID_DEFAULTS = {
    vertical: false,
    strokeDasharray: "3 3",
    stroke: "rgb(var(--border-base) / var(--border-base-baseline-opacity))",
} as const;

// ---------------------------------------------------------------------------
// Axes
// ---------------------------------------------------------------------------

/** Default props for `<XAxis />`. Hides tick marks, shows a solid baseline. */
export const XAXIS_DEFAULTS = {
    tickLine: false,
    axisLine: { stroke: "rgb(var(--content-tertiary))", strokeWidth: 1 },
    tickMargin: 8,
} as const;

/** Default props for `<YAxis />`. Hides tick marks and axis line. */
export const YAXIS_DEFAULTS = {
    tickLine: false,
    axisLine: false,
    tickMargin: 8,
} as const;

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

/** Default tooltip props — no cursor line. */
export const TOOLTIP_DEFAULTS = {
    cursor: false as const,
};

/**
 * Default cursor style for `<ChartTooltip />`.
 *
 * Kept for backward compatibility. New charts should prefer
 * `TOOLTIP_DEFAULTS` (no cursor).
 */
export const TOOLTIP_CURSOR_DEFAULTS = {
    stroke: "rgb(var(--border-base))",
    strokeWidth: 1,
} as const;

// ---------------------------------------------------------------------------
// Chart margins
// ---------------------------------------------------------------------------

/** Default margin for all chart types. */
export const CHART_MARGIN_DEFAULTS = {
    top: 10,
    right: 10,
    left: 0,
    bottom: 0,
} as const;

// ---------------------------------------------------------------------------
// Area gradient
// ---------------------------------------------------------------------------

/** Standard opacity stops for area chart gradient fills. */
export const AREA_GRADIENT_STOPS = {
    topOpacity: 0.35,
    bottomOpacity: 0.06,
} as const;
